/**
 * 视频上传组件 - 使用 Cloudflare Stream + TUS 协议
 *
 * 第一性原理分析：
 * 1. 核心流程：文件选择 → 获取上传凭证 → TUS上传 → 返回视频ID
 * 2. 关键创新：断点续传（TUS协议）解决大文件上传可靠性问题
 * 3. 安全机制：临时签名URL（1小时有效期）防止未授权上传
 */

import React, { useEffect, useState } from 'react';
import { Upload, Progress, Button, Card, Space, Typography, App, Modal } from 'antd';
import { UploadOutlined, VideoCameraOutlined, CheckCircleOutlined, CloseCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import * as tus from 'tus-js-client';
import { supabaseClient } from '../../utility/supabaseClient';

const { Text } = Typography;

interface VideoUploadProps {
  movieId?: string;
  value?: string; // cloudflare_uid
  onChange?: (cloudflareUid: string) => void;
  maxSizeGB?: number;
  maxDurationSeconds?: number;
}

interface UploadProgress {
  percent: number;
  bytesUploaded: number;
  bytesTotal: number;
}

export const VideoUpload: React.FC<VideoUploadProps> = ({
  movieId,
  value,
  onChange,
  maxSizeGB = 5,
  maxDurationSeconds = 7200, // 2 hours
}) => {
  const { message } = App.useApp(); // 使用App的message，支持动态主题
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress>({
    percent: 0,
    bytesUploaded: 0,
    bytesTotal: 0,
  });
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [cloudflareUid, setCloudflareUid] = useState<string | undefined>(value);
  const [uploadError, setUploadError] = useState<string | undefined>();

  const syncUploadedState = (uid: string, filename?: string) => {
    setCloudflareUid(uid);
    setFileList([
      {
        uid,
        name: filename || '已上传视频.mp4',
        status: 'done',
        url: `https://cloudflarestream.com/${uid}`,
      },
    ]);
  };

  // 同步外部传入的默认值
  useEffect(() => {
    if (value) {
      syncUploadedState(value);
    }
  }, [value]);

  // 进入编辑页时自动读取已上传的视频
  useEffect(() => {
    if (!movieId || value) return;

    let active = true;

    const fetchExistingVideo = async () => {
      const { data, error } = await supabaseClient
        .from('movies_videos')
        .select('cloudflare_uid, original_filename')
        .eq('movie_id', movieId)
        .order('uploaded_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (!active) return;

      if (error) {
        console.warn('无法读取已上传视频：', error.message);
        return;
      }

      if (data?.cloudflare_uid) {
        syncUploadedState(data.cloudflare_uid, data.original_filename || undefined);
        if (onChange) {
          onChange(data.cloudflare_uid);
        }
      }
    };

    fetchExistingVideo();

    return () => {
      active = false;
    };
  }, [movieId, value]);


  /**
   * 第一性原理：上传流程分解
   *
   * [用户选择文件]
   *   ↓ (因为：需要Cloudflare临时上传凭证)
   * [调用Edge Function获取uploadURL]
   *   ↓ (关键创新：使用TUS协议实现断点续传)
   * [TUS客户端上传到Cloudflare]
   *   ↓ (因为：异步转码需要状态追踪)
   * [Webhook自动更新数据库]
   *   ↓
   * [返回cloudflareUid给表单]
   */
  const handleUpload = async (file: File) => {
    // 1. 文件验证
    const maxSize = maxSizeGB * 1024 * 1024 * 1024; // Convert GB to bytes
    if (file.size > maxSize) {
      message.error(`文件大小不能超过 ${maxSizeGB}GB`);
      return false;
    }

    // 验证文件类型
    const validTypes = ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/webm'];
    if (!validTypes.includes(file.type)) {
      message.error('仅支持 MP4, MOV, AVI, WebM 格式的视频文件');
      return false;
    }

    setUploading(true);
    setUploadError(undefined);
    setUploadProgress({ percent: 0, bytesUploaded: 0, bytesTotal: file.size });

    try {
      // 2. 调用Edge Function获取上传凭证
      const { data: authData, error: authError } = await supabaseClient.auth.getSession();
      if (authError || !authData.session) {
        throw new Error('请先登录');
      }

      const uploadEndpoint = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/upload-video`;

      const response = await fetch(uploadEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authData.session.access_token}`,
        },
        body: JSON.stringify({
          filename: file.name,
          movieId: movieId,
          maxDurationSeconds: maxDurationSeconds,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || '获取上传凭证失败');
      }

      const { uploadURL, cloudflareUid: cfUid } = await response.json();
      const isTusEndpoint = /\/tus\//.test(uploadURL);

      if (isTusEndpoint) {
        // 3A. 使用TUS协议上传（Cloudflare返回tus专用URL）
        const upload = new tus.Upload(file, {
          uploadUrl: uploadURL,
          storeFingerprintForResuming: false,
          retryDelays: [0, 3000, 5000, 10000, 20000],
          metadata: {
            filename: file.name,
            filetype: file.type,
          },
          onError: (error) => {
            console.error('Upload failed:', error);
            setUploadError(error.message);
            message.error(`上传失败: ${error.message}`);
            setUploading(false);
          },
          onProgress: (bytesUploaded, bytesTotal) => {
            const percent = Math.round((bytesUploaded / bytesTotal) * 100);
            setUploadProgress({
              percent,
              bytesUploaded,
              bytesTotal,
            });
          },
          onSuccess: () => {
            message.success('视频上传成功！Cloudflare 正在转码处理...');
            syncUploadedState(cfUid, file.name);
            setUploading(false);

            if (onChange) {
              onChange(cfUid);
            }
          },
        });

        upload.start();
      } else {
        // 3B. 降级到Basic Upload（Cloudflare仅返回单次POST上传URL）
        await new Promise<void>((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open('POST', uploadURL, true);
          const formData = new FormData();
          formData.append('file', file, file.name);

          xhr.upload.onprogress = (event) => {
            if (!event.lengthComputable) return;
            const percent = Math.round((event.loaded / event.total) * 100);
            setUploadProgress({
              percent,
              bytesUploaded: event.loaded,
              bytesTotal: event.total,
            });
          };

          xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              resolve();
            } else {
              reject(new Error(xhr.responseText || '上传失败'));
            }
          };

          xhr.onerror = () => reject(new Error('网络错误，上传失败'));
          xhr.send(formData);
        });

        message.success('视频上传成功！Cloudflare 正在转码处理...');
        syncUploadedState(cfUid, file.name);
        setUploading(false);

        if (onChange) {
          onChange(cfUid);
        }
      }

    } catch (error: any) {
      console.error('Upload initialization failed:', error);
      setUploadError(error.message);
      message.error(error.message || '上传初始化失败');
      setUploading(false);
    }

    return false; // 阻止 Ant Design 默认上传行为
  };

  /**
   * 删除视频（从 Cloudflare + 数据库）
   */
  const handleDeleteVideo = async (cfUid: string) => {
    // 1. 获取视频记录 ID
    const { data: videoData, error: fetchError } = await supabaseClient
      .from('movies_videos')
      .select('id')
      .eq('cloudflare_uid', cfUid)
      .maybeSingle();

    if (fetchError) {
      throw new Error(`查询视频失败: ${fetchError.message}`);
    }

    // 2. 调用删除 Edge Function
    const { data: authData, error: authError } = await supabaseClient.auth.getSession();
    if (authError || !authData.session) {
      throw new Error('请先登录');
    }

    const deleteEndpoint = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/delete-movie-video`;

    const response = await fetch(deleteEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authData.session.access_token}`,
      },
      body: JSON.stringify({
        movieId: movieId,
        videoId: videoData?.id,
        cloudflareUid: cfUid,
      }),
    });

    if (!response.ok) {
      // 尝试解析 JSON 错误，如果失败则使用 HTTP 状态文本
      let errorMessage = '删除失败';
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorMessage;
      } catch (parseError) {
        // 如果响应不是 JSON（Edge Function 崩溃），使用状态文本
        const text = await response.text();
        errorMessage = `服务器错误 (${response.status}): ${text.slice(0, 200) || response.statusText}`;
        console.error('Edge Function 返回非 JSON 响应:', text);
      }
      throw new Error(errorMessage);
    }

    const result = await response.json();
    if (!result.success) {
      throw new Error(result.error || '删除失败');
    }
  };

  // 格式化文件大小
  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <div>
      <Upload
        beforeUpload={handleUpload}
        fileList={fileList}
        showUploadList={false}
        maxCount={1}
        accept="video/mp4,video/quicktime,video/x-msvideo,video/webm"
      >
        <Button icon={<UploadOutlined />} disabled={uploading || !!cloudflareUid}>
          {cloudflareUid ? '已上传视频' : '选择视频文件'}
        </Button>
      </Upload>

      {/* 上传进度显示 */}
      {uploading && (
        <Card size="small" style={{ marginTop: 16 }}>
          <Space direction="vertical" style={{ width: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <VideoCameraOutlined style={{ fontSize: 24, color: '#1890ff' }} />
              <Text strong>正在上传到 Cloudflare Stream...</Text>
            </div>

            <Progress
              percent={uploadProgress.percent}
              status="active"
              strokeColor={{
                '0%': '#108ee9',
                '100%': '#87d068',
              }}
            />

            <Text type="secondary">
              已上传: {formatBytes(uploadProgress.bytesUploaded)} / {formatBytes(uploadProgress.bytesTotal)}
            </Text>
          </Space>
        </Card>
      )}

      {/* 成功状态 */}
      {cloudflareUid && !uploading && (
        <Card size="small" style={{ marginTop: 16, borderColor: '#52c41a' }}>
          <Space direction="vertical" style={{ width: '100%' }}>
            <Space style={{ justifyContent: 'space-between', width: '100%' }}>
              <Space>
                <CheckCircleOutlined style={{ fontSize: 20, color: '#52c41a' }} />
                <div>
                  <Text strong style={{ color: '#52c41a' }}>视频上传成功</Text>
                  <br />
                  <Text type="secondary" copyable={{ text: cloudflareUid }}>
                    Cloudflare UID: {cloudflareUid}
                  </Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    提示：Cloudflare 正在后台转码，稍后即可播放
                  </Text>
                </div>
              </Space>
              <Button
                danger
                onClick={() => {
                  Modal.confirm({
                    title: '确定要删除这个视频吗？',
                    icon: <ExclamationCircleOutlined />,
                    content: (
                      <div>
                        <p style={{ marginBottom: 8 }}>此操作将：</p>
                        <ul style={{ paddingLeft: 20, marginBottom: 0 }}>
                          <li>从 Cloudflare Stream 删除视频文件</li>
                          <li>从数据库删除视频记录</li>
                        </ul>
                        <p style={{ marginTop: 8, color: '#ff4d4f', fontWeight: 'bold' }}>
                          ⚠️ 此操作不可恢复！
                        </p>
                      </div>
                    ),
                    okText: '确定删除',
                    okType: 'danger',
                    cancelText: '取消',
                    async onOk() {
                      try {
                        await handleDeleteVideo(cloudflareUid);
                        message.success('视频删除成功');
                        setFileList([]);
                        setCloudflareUid(undefined);
                        setUploadProgress({ percent: 0, bytesUploaded: 0, bytesTotal: 0 });
                        if (onChange) onChange('');
                      } catch (error: any) {
                        message.error(`删除失败: ${error.message}`);
                        throw error; // 让 Modal 保持打开状态
                      }
                    },
                  });
                }}
              >
                🗑️ 删除视频
              </Button>
            </Space>
          </Space>
        </Card>
      )}

      {/* 错误状态 */}
      {uploadError && !uploading && (
        <Card size="small" style={{ marginTop: 16, borderColor: '#ff4d4f' }}>
          <Space>
            <CloseCircleOutlined style={{ fontSize: 20, color: '#ff4d4f' }} />
            <div>
              <Text strong style={{ color: '#ff4d4f' }}>上传失败</Text>
              <br />
              <Text type="secondary">{uploadError}</Text>
            </div>
          </Space>
        </Card>
      )}

      {/* 使用说明 */}
      <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 8 }}>
        支持格式：MP4, MOV, AVI, WebM | 最大文件：{maxSizeGB}GB | 最长时长：{Math.floor(maxDurationSeconds / 60)}分钟
      </Text>
    </div>
  );
};
